/**
 * Procurement status chips (procurement.md §4): one on the case header, one on each queue row, and the PO status
 * ("awaiting Books approval") wherever a PO shows. Owner: web-procurement.
 */
import { Chip, type ChipTone } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { Package } from 'lucide-react'
import { isFeatureOff, type PoStatus, type ProcurementStatus, procurementApi, procurementKeys } from './api'
import { formatEta } from './tracking'

export const PROCUREMENT_LABEL: Record<ProcurementStatus, string> = {
  awaiting_approval: 'PO awaiting Books approval',
  ordered: 'Parts ordered',
  shipped: 'Parts shipped',
  partially_received: 'Parts partly received',
  staged: 'Parts staged at office',
  received: 'Parts received',
  installed: 'Parts installed',
  billed: 'Hardware billed',
  rejected: 'PO rejected in Books',
  cancelled: 'Order cancelled',
}

const PROCUREMENT_TONE: Record<ProcurementStatus, ChipTone> = {
  awaiting_approval: 'warning',
  ordered: 'primary',
  shipped: 'primary',
  partially_received: 'warning',
  staged: 'warning',
  received: 'success',
  installed: 'success',
  billed: 'neutral',
  rejected: 'danger',
  cancelled: 'neutral',
}

export const PO_LABEL: Record<PoStatus, string> = {
  draft: 'PO draft (not submitted)',
  pending_approval: 'Awaiting Books approval',
  approved: 'Approved in Books',
  rejected: 'Rejected in Books',
  issued: 'Issued to vendor',
  partially_received: 'Partly received',
  received: 'Received',
  billed: 'Billed in Books',
  cancelled: 'Cancelled',
}

const PO_TONE: Record<PoStatus, ChipTone> = {
  draft: 'neutral',
  pending_approval: 'warning',
  approved: 'success',
  rejected: 'danger',
  issued: 'primary',
  partially_received: 'warning',
  received: 'success',
  billed: 'neutral',
  cancelled: 'neutral',
}

export function ProcurementChip({
  status,
  eta,
  overdue,
}: {
  status: ProcurementStatus | null | undefined
  eta?: string | null
  overdue?: boolean
}) {
  if (!status) return null
  const label = PROCUREMENT_LABEL[status] ?? status.replaceAll('_', ' ')
  const etaText = (status === 'ordered' || status === 'shipped') && eta ? ` · ETA ${formatEta(eta)}` : ''
  return (
    <Chip tone={overdue ? 'danger' : (PROCUREMENT_TONE[status] ?? 'outline')} data-procurement={status} title="Procurement status">
      <Package aria-hidden />
      {overdue ? `Shipment overdue${etaText}` : `${label}${etaText}`}
    </Chip>
  )
}

export function PoStatusChip({ status, errorCode }: { status: PoStatus; errorCode?: string | null }) {
  if (errorCode === 'books_scope_missing') {
    return (
      <Chip tone="danger" title="The Books token lacks the purchaseorders scope">
        Books refused the PO (scope missing)
      </Chip>
    )
  }
  return <Chip tone={PO_TONE[status] ?? 'outline'}>{PO_LABEL[status] ?? status}</Chip>
}

/** Queue rows may eventually carry these additive fields. Until then the chip reads the existing case endpoint. */
export function procurementOfRow(row: object): { status: ProcurementStatus | null; eta: string | null; overdue: boolean } {
  const r = row as { procurement_status?: ProcurementStatus | null; procurement_eta?: string | null; procurement_overdue?: boolean }
  return { status: r.procurement_status ?? null, eta: r.procurement_eta ?? null, overdue: Boolean(r.procurement_overdue) }
}

export function QueueProcurementChip({ row }: { row: object }) {
  const source = row as {
    id?: string
    procurement_status?: ProcurementStatus | null
    procurement_eta?: string | null
    procurement_overdue?: boolean
  }
  const embedded = 'procurement_status' in source || 'procurement_eta' in source || 'procurement_overdue' in source
  const p = procurementOfRow(row)
  const q = useQuery({
    queryKey: procurementKeys.case(source.id ?? ''),
    queryFn: () => procurementApi.caseProcurement(source.id ?? ''),
    enabled: !embedded && Boolean(source.id),
    retry: 1,
  })
  if (embedded) return <ProcurementChip status={p.status} eta={p.eta} overdue={p.overdue} />
  if (!source.id || q.isPending || (q.isError && isFeatureOff(q.error))) return null
  if (q.isError) {
    return (
      <Chip tone="warning" title="Procurement lookup failed; parts state is unknown">
        <Package aria-hidden /> Parts unknown
      </Chip>
    )
  }
  const overdue = q.data.orders.some((o) => o.shipments.some((s) => s.status !== 'delivered' && isOverdue(s.eta)))
  return <ProcurementChip status={q.data.status} eta={q.data.eta} overdue={overdue} />
}

/** The case header chip. Shares the rail section's query (one request); a route that is not deployed yet hides it. */
export function CaseProcurementChip({ caseId }: { caseId: string }) {
  const q = useQuery({ queryKey: procurementKeys.case(caseId), queryFn: () => procurementApi.caseProcurement(caseId), retry: 1 })
  if (q.isPending || (q.isError && isFeatureOff(q.error))) return null
  if (q.isError) {
    return (
      <Chip tone="warning" title="Procurement lookup failed; the state is unknown, not empty">
        <Package aria-hidden /> Procurement unreachable
      </Chip>
    )
  }
  const overdue = q.data.orders.some((o) => o.shipments.some((s) => s.status !== 'delivered' && isOverdue(s.eta)))
  return <ProcurementChip status={q.data.status} eta={q.data.eta} overdue={overdue} />
}

/** ETA + 1 business day has passed (procurement.md §2 shipment_overdue). The API's flag wins where it is given. */
export function isOverdue(eta: string | null | undefined, now: Date = new Date()): boolean {
  if (!eta) return false
  const base = /^\d{4}-\d{2}-\d{2}$/.test(eta) ? new Date(`${eta}T23:59:59`) : new Date(eta)
  if (Number.isNaN(base.getTime())) return false
  const due = new Date(base)
  do due.setDate(due.getDate() + 1)
  while (due.getDay() === 0 || due.getDay() === 6)
  return now > due
}
